import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { inr } from "@/lib/utils-format";
import { AlertTriangle } from "lucide-react";

export const POS_METHODS = [
  { k: "cash", label: "Cash" }, { k: "gpay", label: "GPay" }, { k: "upi", label: "UPI" }, { k: "phonepe", label: "PhonePe" },
  { k: "card", label: "Card" }, { k: "wallet", label: "Wallet" }, { k: "credit", label: "Credit (udhaar)" },
];
const label = (k) => POS_METHODS.find((m) => m.k === k)?.label || k;

// Super Admin: change the payment method on a completed POS bill (amount stays the same, ledger re-posted)
export function PosPaymentEditDialog({ sale, open, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && sale) {
      setRows((sale.payments || []).map((p) => ({ method: p.method, amount: Number(p.amount || 0), reference: p.reference || "" })));
      setReason(""); setConfirm(false);
    }
  }, [open, sale]);
  if (!sale) return null;
  const changed = rows.some((r, i) => r.method !== (sale.payments?.[i]?.method));
  const summary = rows.map((r, i) => {
    const from = sale.payments?.[i]?.method;
    return from !== r.method ? `${label(from)} → ${label(r.method)} (${inr(r.amount)})` : null;
  }).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/pos/sales/${sale.id}/payments`, { payments: rows, reason: reason || null });
      toast.success(`${sale.number} — payment method updated & ledger corrected`);
      onSaved?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update payment method"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="pos-payment-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Edit payment method · {sale.number}</DialogTitle>
          <DialogDescription>Only the method can change — amounts stay as billed. The Cash / GPay / bank ledger is corrected automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`pos-payment-edit-row-${i}`}>
              <Select value={r.method} onValueChange={(v) => setRows((prev) => prev.map((x, j) => j === i ? { ...x, method: v } : x))} disabled={r.method === "credit"}>
                <SelectTrigger className="w-40 bg-white" data-testid={`pos-payment-edit-method-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {POS_METHODS.filter((m) => m.k !== "credit" || r.method === "credit").map((m) => <SelectItem key={m.k} value={m.k}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex-1 text-right font-semibold tabular-nums">{inr(r.amount)}</div>
              <Input value={r.reference} onChange={(e) => setRows((prev) => prev.map((x, j) => j === i ? { ...x, reference: e.target.value } : x))}
                     placeholder="Ref" className="w-24 h-9 text-xs" data-testid={`pos-payment-edit-ref-${i}`} />
            </div>
          ))}
          <div><Label className="text-xs">Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. cashier picked Cash by mistake, customer paid via GPay" data-testid="pos-payment-edit-reason" /></div>
        </div>
        {confirm ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="pos-payment-edit-confirm">
            <div className="flex items-start gap-2 text-sm text-amber-900">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Confirm change on {sale.number}?</div>
                <ul className="list-disc ml-4 mt-1">{summary.map((s, i) => <li key={i}>{s}</li>)}</ul>
                <div className="mt-1 text-xs">The original ledger posting is reversed and re-posted to the correct account. This is audited.</div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirm(false)} disabled={busy} data-testid="pos-payment-edit-back">Back</Button>
              <Button onClick={save} disabled={busy} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="pos-payment-edit-confirm-btn">{busy ? "Saving…" : "Yes, update"}</Button>
            </div>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="pos-payment-edit-cancel">Cancel</Button>
            <Button onClick={() => setConfirm(true)} disabled={!changed} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="pos-payment-edit-save">Save change</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
